import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { authService } from '../services/authService';
import { useTecnicos } from '../hooks/useTecnicos';
import { TIPOS_AUSENCIA, getTipoAusencia as getTipo } from '../utils/tiposAusencia';

// ── Helpers de fecha ──────────────────────────────────────────────────────────
const getSpainToday  = () => new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Madrid' }).format(new Date());
const getSpainMonth  = () => getSpainToday().slice(0, 7);

const adjustDate = (d, delta) => {
  const date = new Date(d + 'T12:00:00Z');
  date.setUTCDate(date.getUTCDate() + delta);
  return date.toISOString().slice(0, 10);
};
const adjustMonth = (mes, delta) => {
  const [y, m] = mes.split('-').map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};
const formatDateLong  = (s) => s ? new Date(s + 'T12:00:00Z').toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) : '';
const formatMonthYear = (mes) => { const [y, m] = mes.split('-'); return new Date(`${y}-${m}-15T12:00:00Z`).toLocaleDateString('es-ES', { month: 'long', year: 'numeric' }); };
const minToDisplay    = (mins) => { if (mins == null) return '—'; const h = Math.floor(mins / 60); const m = mins % 60; return `${h}h ${String(m).padStart(2, '0')}m`; };

// ── Subcomponentes ────────────────────────────────────────────────────────────
function EstadoBadge({ fichaje }) {
  if (!fichaje) return <span className="inline-flex items-center gap-1 text-xs font-semibold text-gray-400"><span className="w-2 h-2 rounded-full bg-gray-300 flex-shrink-0" />Sin fichar</span>;
  if (fichaje.estado === 'activo') return <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700"><span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse flex-shrink-0" />En jornada</span>;
  if (fichaje.estado === 'corregido') return <span className="inline-flex items-center gap-1 text-xs font-semibold text-blue-700"><span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />Corregido</span>;
  return <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-700"><span className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0" />Cerrado</span>;
}

// ── Modal fichaje ─────────────────────────────────────────────────────────────
function FichajeModal({ fichaje, tecnico, fecha, onClose, onSaved, userName }) {
  const isNew = !fichaje;
  const [entradaHora, setEntradaHora] = useState(fichaje?.entradaHora || '');
  const [salidaHora,  setSalidaHora]  = useState(fichaje?.salidaHora  || '');
  const [motivo,      setMotivo]      = useState('');
  const [saving,      setSaving]      = useState(false);
  const [error,       setError]       = useState('');

  const handleSave = async () => {
    if (!entradaHora) { setError('La hora de entrada es obligatoria'); return; }
    if (salidaHora && salidaHora <= entradaHora) { setError('La salida debe ser posterior a la entrada'); return; }
    if (!isNew && !motivo.trim()) { setError('El motivo de corrección es obligatorio'); return; }
    setSaving(true); setError('');
    try {
      if (isNew) {
        const r1 = await fetch('/.netlify/functions/createFichaje', {
          method: 'POST', headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
          body: JSON.stringify({
            tipo: 'entrada', tecnicoId: String(tecnico.id), tecnicoNombre: tecnico.nombre,
            fecha, horaManual: entradaHora, timestampManual: new Date(`${fecha}T${entradaHora}:00`).toISOString(),
            creadoPor: userName || 'coordinador',
          }),
        });
        const d1 = await r1.json();
        if (!d1.success) throw new Error(d1.error);
        if (salidaHora && d1.fichaje?.id) {
          await fetch('/.netlify/functions/updateFichaje', {
            method: 'POST', headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
            body: JSON.stringify({
              fichajeId: d1.fichaje.id, cambios: { entradaHora, salidaHora },
              motivo: 'Fichaje creado manualmente con entrada y salida', usuarioEditor: userName || 'coordinador',
            }),
          });
        }
      } else {
        const cambios = { entradaHora };
        if (salidaHora) cambios.salidaHora = salidaHora;
        const r = await fetch('/.netlify/functions/updateFichaje', {
          method: 'POST', headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
          body: JSON.stringify({ fichajeId: fichaje.id, cambios, motivo: motivo.trim(), usuarioEditor: userName || 'coordinador' }),
        });
        const d = await r.json();
        if (!d.success) throw new Error(d.error);
      }
      onSaved();
    } catch (e) { setError(e.message); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-gray-100">
          <h3 className="font-bold text-gray-900">{isNew ? 'Añadir fichaje manual' : 'Corregir fichaje'}</h3>
          <p className="text-xs text-gray-400 mt-0.5 capitalize">{tecnico.nombre} · {formatDateLong(fecha)}</p>
        </div>
        <div className="px-5 py-4 space-y-4">
          {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-3 py-2 text-sm">{error}</div>}
          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Hora de entrada *</label>
            <input type="time" value={entradaHora} onChange={e => setEntradaHora(e.target.value)} className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400 focus:ring-1 focus:ring-red-200" />
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Hora de salida <span className="text-gray-400 normal-case font-normal">(opcional)</span></label>
            <input type="time" value={salidaHora} onChange={e => setSalidaHora(e.target.value)} className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400 focus:ring-1 focus:ring-red-200" />
          </div>
          {!isNew && (
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Motivo de corrección *</label>
              <input type="text" value={motivo} onChange={e => setMotivo(e.target.value)} placeholder="Ej: Olvido de fichar salida" className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400 focus:ring-1 focus:ring-red-200" />
              <p className="text-xs text-gray-400 mt-1">Requerido por trazabilidad legal (RDL 8/2019)</p>
            </div>
          )}
        </div>
        <div className="px-5 pb-5 flex gap-3 justify-end">
          <button onClick={onClose} className="px-4 py-2.5 text-sm font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors">Cancelar</button>
          <button onClick={handleSave} disabled={saving} className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 rounded-xl transition-colors">
            {saving && <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />}
            {isNew ? 'Crear fichaje' : 'Guardar corrección'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Modal ausencia ────────────────────────────────────────────────────────────
function AusenciaModal({ ausencia, fechaDefecto, tecnicoPreset, allUsers, onClose, onSaved, userName }) {
  const isEdit = !!ausencia;
  const defaultId = ausencia?.tecnicoId || tecnicoPreset?.id || (allUsers[0] ? String(allUsers[0].employeeId || allUsers[0].id) : '');
  const [tecnicoId,   setTecnicoId]   = useState(defaultId);
  const [tipo,        setTipo]        = useState(ausencia?.tipo || 'vacaciones');
  const [fechaInicio, setFechaInicio] = useState(ausencia?.fechaInicio || fechaDefecto || getSpainToday());
  const [fechaFin,    setFechaFin]    = useState(ausencia?.fechaFin    || fechaDefecto || getSpainToday());
  const [notas,       setNotas]       = useState(ausencia?.notas || '');
  const [saving,      setSaving]      = useState(false);
  const [error,       setError]       = useState('');

  const getUserId = (u) => u.role === 'tecnico' && u.employeeId ? String(u.employeeId) : String(u.id);
  const selectedUser = allUsers.find(u => getUserId(u) === String(tecnicoId)) || allUsers[0];
  const tecnico = selectedUser ? { id: getUserId(selectedUser), nombre: selectedUser.name } : { id: tecnicoId, nombre: '' };

  const handleSave = async () => {
    if (!fechaInicio || !fechaFin) { setError('Las fechas son obligatorias'); return; }
    if (fechaFin < fechaInicio)    { setError('La fecha fin debe ser igual o posterior a la fecha inicio'); return; }
    setSaving(true); setError('');
    try {
      if (isEdit) {
        // Borrar y volver a crear (Firestore REST no tiene update fácil sin ID en URL)
        await fetch(`/.netlify/functions/manageAusencias?id=${ausencia.id}`, { method: 'DELETE', headers: { ...authService.authHeader() } });
      }
      const r = await fetch('/.netlify/functions/manageAusencias', {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify({ tecnicoId: String(tecnico.id), tecnicoNombre: tecnico.nombre, tipo, fechaInicio, fechaFin, notas, creadoPor: userName || 'coordinador' }),
      });
      const d = await r.json();
      if (!d.success) throw new Error(d.error);
      onSaved();
    } catch (e) { setError(e.message); } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!isEdit) return;
    setSaving(true);
    try {
      await fetch(`/.netlify/functions/manageAusencias?id=${ausencia.id}`, { method: 'DELETE', headers: { ...authService.authHeader() } });
      onSaved();
    } catch (e) { setError(e.message); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-gray-100">
          <h3 className="font-bold text-gray-900">{isEdit ? 'Editar ausencia' : 'Registrar ausencia'}</h3>
        </div>
        <div className="px-5 py-4 space-y-4">
          {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-3 py-2 text-sm">{error}</div>}

          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Técnico</label>
            <select value={tecnicoId} onChange={e => setTecnicoId(e.target.value)} disabled={isEdit}
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400 focus:ring-1 focus:ring-red-200 disabled:bg-gray-50">
              {allUsers.map(u => <option key={u.id} value={getUserId(u)}>{u.name}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Tipo de ausencia</label>
            <select value={tipo} onChange={e => setTipo(e.target.value)}
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400 focus:ring-1 focus:ring-red-200">
              {TIPOS_AUSENCIA.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Fecha inicio</label>
              <input type="date" value={fechaInicio} onChange={e => setFechaInicio(e.target.value)} className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400 focus:ring-1 focus:ring-red-200" />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Fecha fin</label>
              <input type="date" value={fechaFin} min={fechaInicio} onChange={e => setFechaFin(e.target.value)} className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400 focus:ring-1 focus:ring-red-200" />
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Notas <span className="text-gray-400 normal-case font-normal">(opcional)</span></label>
            <textarea value={notas} onChange={e => setNotas(e.target.value)} rows={2} placeholder="Ej: Baja por cirugía, aprobado el 12/05..." className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400 focus:ring-1 focus:ring-red-200 resize-none" />
          </div>
        </div>
        <div className="px-5 pb-5 flex gap-3">
          {isEdit && (
            <button onClick={handleDelete} disabled={saving} className="px-4 py-2.5 text-sm font-semibold text-red-600 bg-red-50 hover:bg-red-100 rounded-xl transition-colors disabled:opacity-50">
              Eliminar
            </button>
          )}
          <div className="flex-1" />
          <button onClick={onClose} className="px-4 py-2.5 text-sm font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors">Cancelar</button>
          <button onClick={handleSave} disabled={saving} className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 rounded-xl transition-colors">
            {saving && <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />}
            Guardar
          </button>
        </div>
      </div>
    </div>
  );
}

// ── PDF generation ────────────────────────────────────────────────────────────
async function generatePDF(selectedMonth, fichajesByTecnicoMonth, ausencias, roster = []) {
  const { jsPDF } = await import('jspdf');
  const [yr, mo] = selectedMonth.split('-').map(Number);

  // Días laborables del mes
  const workdays = [];
  for (let d = 1; d <= 31; d++) {
    const date = new Date(yr, mo - 1, d);
    if (date.getMonth() !== mo - 1) break;
    if (date.getDay() === 0 || date.getDay() === 6) continue;
    workdays.push({ d, dateStr: `${yr}-${String(mo).padStart(2,'0')}-${String(d).padStart(2,'0')}`, dow: date.getDay() });
  }

  const DIAS = ['Dom','Lun','Mar','Mié','Jue','Vie','Sáb'];
  const mesBonito = formatMonthYear(selectedMonth);

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

  roster.forEach((tecnico, idx) => {
    if (idx > 0) doc.addPage();

    const tecMap = fichajesByTecnicoMonth[String(tecnico.id)] || {};

    // Ausencias del técnico para este mes
    const tecAusencias = ausencias.filter(a =>
      String(a.tecnicoId) === String(tecnico.id) &&
      a.fechaFin >= `${yr}-${String(mo).padStart(2,'0')}-01` &&
      a.fechaInicio <= `${yr}-${String(mo).padStart(2,'0')}-31`
    );
    const getAusencia = (dateStr) => tecAusencias.find(a => a.fechaInicio <= dateStr && a.fechaFin >= dateStr);

    // ── Cabecera ──────────────────────────────────────────────
    doc.setFillColor(220, 38, 38);
    doc.rect(0, 0, 210, 22, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.text('REGISTRO DE JORNADA LABORAL', 105, 10, { align: 'center' });
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text('Climatización Zapata · RDL 8/2019 · Art. 34.9 ET', 105, 17, { align: 'center' });

    // ── Info empleado ─────────────────────────────────────────
    doc.setTextColor(30, 30, 30);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text(`Empleado: ${tecnico.nombre}`, 14, 30);
    doc.text(`Período: ${mesBonito}`, 14, 37);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.text(`ID empleado: ${tecnico.id}`, 14, 43);
    doc.text(`Empresa: Climatización Zapata`, 120, 30);
    doc.text(`Generado: ${new Date().toLocaleDateString('es-ES')}`, 120, 37);

    // Línea separadora
    doc.setDrawColor(220, 38, 38);
    doc.setLineWidth(0.5);
    doc.line(14, 46, 196, 46);

    // ── Cabecera de tabla ─────────────────────────────────────
    let y = 55;
    const cols = { fecha: 14, dia: 44, entrada: 70, salida: 100, jornada: 130, estado: 158 };

    doc.setFillColor(245, 245, 245);
    doc.rect(14, y - 5, 182, 7, 'F');
    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(80, 80, 80);
    doc.text('Fecha',    cols.fecha,   y);
    doc.text('Día',      cols.dia,     y);
    doc.text('Entrada',  cols.entrada, y);
    doc.text('Salida',   cols.salida,  y);
    doc.text('Jornada',  cols.jornada, y);
    doc.text('Observaciones', cols.estado, y);
    doc.setTextColor(30, 30, 30);
    y += 5;

    // ── Filas de datos ────────────────────────────────────────
    let totalMin = 0;
    let diasTrabajados = 0;

    workdays.forEach(({ d, dateStr, dow }, rowIdx) => {
      if (y > 255) {
        doc.addPage();
        y = 20;
      }

      if (rowIdx % 2 === 0) {
        doc.setFillColor(252, 252, 252);
        doc.rect(14, y - 4, 182, 6.5, 'F');
      }

      const fechaES = `${String(d).padStart(2,'0')}/${String(mo).padStart(2,'0')}/${yr}`;
      const ausencia = getAusencia(dateStr);
      const fichaje  = tecMap[dateStr];
      const tipo     = ausencia ? getTipo(ausencia.tipo) : null;

      doc.setFontSize(7.5);
      doc.setFont('helvetica', 'normal');

      // Fecha y día
      doc.setTextColor(50, 50, 50);
      doc.text(fechaES, cols.fecha, y);
      doc.text(DIAS[dow], cols.dia, y);

      if (ausencia) {
        // Ausencia
        doc.setTextColor(120, 80, 200);
        doc.setFont('helvetica', 'bold');
        doc.text('—', cols.entrada, y);
        doc.text('—', cols.salida,  y);
        doc.text('—', cols.jornada, y);
        doc.text(tipo.label, cols.estado, y);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(50, 50, 50);
      } else if (fichaje) {
        diasTrabajados++;
        const durEfectivo = fichaje.duracionMinutos || 0;
        const durDescuento = fichaje.duracionDescansoMinutos || 0;
        totalMin += durEfectivo;
        doc.text(fichaje.entradaHora || '', cols.entrada, y);
        doc.text(fichaje.salidaHora  || '', cols.salida,  y);
        const jornadaText = durEfectivo > 0 ? minToDisplay(durEfectivo) + (durDescuento > 0 ? ' ef.' : '') : '—';
        doc.text(jornadaText, cols.jornada, y);
        if (fichaje.estado === 'corregido') {
          doc.setTextColor(59, 130, 246);
          doc.text('Corregido', cols.estado, y);
          doc.setTextColor(50, 50, 50);
        } else {
          doc.text(fichaje.salidaHora ? 'Cerrado' : 'Activo', cols.estado, y);
        }
      }
      // Línea delimitadora fila
      doc.setDrawColor(230, 230, 230);
      doc.setLineWidth(0.1);
      doc.line(14, y + 2.5, 196, y + 2.5);

      y += 6.5;
    });

    // ── Resumen ───────────────────────────────────────────────
    y += 3;
    doc.setFillColor(245, 245, 245);
    doc.rect(14, y - 4, 182, 16, 'F');
    doc.setFontSize(8.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(30, 30, 30);
    doc.text('Resumen del mes:', 16, y);
    y += 6;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.text(`Días trabajados: ${diasTrabajados}`, 16, y);
    doc.text(`Total efectivo: ${minToDisplay(totalMin)} (descanso 1h/día deducido automáticamente)`, 80, y);
    if (tecAusencias.length > 0) {
      const resumenAus = tecAusencias.map(a => `${getTipo(a.tipo).label} (${a.fechaInicio} → ${a.fechaFin})`).join(' | ');
      y += 5;
      doc.text(`Ausencias: ${resumenAus}`, 16, y);
    }

    // ── Firma ─────────────────────────────────────────────────
    const sigY = 260;
    doc.setDrawColor(100, 100, 100);
    doc.setLineWidth(0.4);
    doc.line(14, sigY, 90, sigY);
    doc.line(120, sigY, 196, sigY);
    doc.setFontSize(8);
    doc.setTextColor(80, 80, 80);
    doc.setFont('helvetica', 'normal');
    doc.text('Firma del empleado', 52, sigY + 4, { align: 'center' });
    doc.text('Firma del responsable', 158, sigY + 4, { align: 'center' });
    doc.setFont('helvetica', 'bold');
    doc.text(tecnico.nombre, 52, sigY + 9, { align: 'center' });
    doc.text('Climatización Zapata', 158, sigY + 9, { align: 'center' });
    doc.setFont('helvetica', 'normal');
    doc.text(`Fecha: ___/___/______`, 52, sigY + 14, { align: 'center' });
    doc.text(`Fecha: ___/___/______`, 158, sigY + 14, { align: 'center' });

    // ── Pie de página ─────────────────────────────────────────
    doc.setFontSize(6.5);
    doc.setTextColor(160, 160, 160);
    doc.text('Documento generado automáticamente · Conforme al Real Decreto-ley 8/2019 y art. 34.9 del Estatuto de los Trabajadores · Conservar 4 años', 105, 285, { align: 'center' });
  });

  doc.save(`registro-jornada-${selectedMonth}.pdf`);
}

// ── Vista principal ───────────────────────────────────────────────────────────
export default function FichajesView() {
  const { user } = useAuth();
  const [tab,           setTab]          = useState('diario');
  const [selectedDate,  setSelectedDate] = useState(getSpainToday());
  const [selectedMonth, setSelectedMonth]= useState(getSpainMonth());
  const [fichajes,      setFichajes]     = useState([]);
  const [ausencias,     setAusencias]    = useState([]);
  const [loading,       setLoading]      = useState(false);
  const [fichajeModal,  setFichajeModal] = useState(null);
  const [ausenciaModal, setAusenciaModal]= useState(null);
  const [pdfLoading,    setPdfLoading]   = useState(false);
  const [allUsers,      setAllUsers]     = useState([]);
  const { tecnicos } = useTecnicos();

  const today = getSpainToday();

  const loadFichajes = useCallback(async () => {
    setLoading(true);
    try {
      const param = tab === 'diario' ? `fecha=${selectedDate}` : `mes=${selectedMonth}`;
      const resp = await fetch(`/.netlify/functions/getFichajes?${param}`, { headers: { ...authService.authHeader() } });
      const data = await resp.json();
      if (data.success) setFichajes(data.fichajes || []);
      else console.warn('[loadFichajes] la API respondió sin éxito:', resp.status, data.error || data);
    } catch (e) { console.error('[loadFichajes]', e); } finally { setLoading(false); }
  }, [tab, selectedDate, selectedMonth]);

  const loadAusencias = useCallback(async () => {
    try {
      const resp = await fetch(`/.netlify/functions/manageAusencias?mes=${selectedMonth}`, { headers: { ...authService.authHeader() } });
      const data = await resp.json();
      if (data.success) setAusencias(data.ausencias || []);
      else console.warn('[loadAusencias] la API respondió sin éxito:', resp.status, data.error || data);
    } catch (e) { console.error('[loadAusencias]', e); }
  }, [selectedMonth]);

  const loadUsers = useCallback(async () => {
    try {
      const resp = await fetch('/.netlify/functions/manageUsers', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify({ action: 'list' }),
      });
      const data = await resp.json();
      if (data.success) setAllUsers(data.data || []);
    } catch (e) { console.error('Error cargando usuarios:', e); }
  }, []);

  useEffect(() => { loadFichajes(); }, [loadFichajes]);
  useEffect(() => { loadAusencias(); }, [loadAusencias]);
  useEffect(() => { loadUsers(); }, [loadUsers]);

  // ── Helpers de datos ──
  // Identificador del fichaje: técnicos usan employeeId, el resto usan id de Firestore
  const getUserFichajeId = (u) =>
    u.role === 'tecnico' && u.employeeId ? String(u.employeeId) : String(u.id);

  const ROLE_GROUPS = [
    { role: 'tecnico',                 label: 'Técnicos de campo'   },
    { role: 'coordinador_operaciones', label: 'Coordinación'        },
    { role: 'atencion_cliente',        label: 'Atención al cliente' },
    { role: 'admin',                   label: 'Administración'      },
  ];

  const roleGroups = useMemo(() =>
    ROLE_GROUPS
      .map(g => ({ ...g, users: allUsers.filter(u => u.role === g.role) }))
      .filter(g => g.users.length > 0),
  [allUsers]);

  const fichajeByTecnico = useMemo(() => {
    const map = {};
    for (const f of fichajes) map[String(f.tecnicoId)] = f;
    return map;
  }, [fichajes]);

  const getAusenciaForDate = useCallback((tecnicoId, fecha) =>
    ausencias.find(a => String(a.tecnicoId) === String(tecnicoId) && a.fechaInicio <= fecha && a.fechaFin >= fecha),
  [ausencias]);

  const fichajesByTecnicoMonth = useMemo(() => {
    const map = {};
    for (const u of allUsers) { const k = getUserFichajeId(u); if (k) map[k] = {}; }
    for (const f of fichajes) {
      if (!map[String(f.tecnicoId)]) map[String(f.tecnicoId)] = {};
      map[String(f.tecnicoId)][f.fecha] = f;
    }
    return map;
  }, [fichajes, allUsers]);

  const monthlyStats = useMemo(() => allUsers.map(u => {
    const uid = getUserFichajeId(u);
    const fics = Object.values(fichajesByTecnicoMonth[uid] || {});
    const totalMin = fics.reduce((s, f) => s + (f.duracionMinutos || 0), 0);
    const ausCount = ausencias.filter(a => {
      if (String(a.tecnicoId) !== uid) return false;
      const [y, m] = selectedMonth.split('-');
      return a.fechaFin >= `${y}-${m}-01` && a.fechaInicio <= `${y}-${m}-31`;
    }).length;
    const grupo = ROLE_GROUPS.find(g => g.role === u.role)?.label || u.role;
    return { nombre: u.name, id: uid, role: u.role, grupo, dias: fics.length, totalMin, ausCount };
  }), [fichajesByTecnicoMonth, ausencias, selectedMonth, allUsers]);

  const calendarDays = useMemo(() => {
    const [y, m] = selectedMonth.split('-').map(Number);
    const days = [];
    for (let d = 1; d <= 31; d++) {
      const date = new Date(y, m - 1, d);
      if (date.getMonth() !== m - 1) break;
      if (date.getDay() === 0 || date.getDay() === 6) continue;
      days.push(`${y}-${String(m).padStart(2,'0')}-${String(d).padStart(2,'0')}`);
    }
    return days;
  }, [selectedMonth]);

  // Ausencias del mes agrupadas por tecnicoId
  const ausenciasByTecnico = useMemo(() => {
    const map = {};
    for (const u of allUsers) { const k = getUserFichajeId(u); if (k) map[k] = []; }
    for (const a of ausencias) {
      const key = String(a.tecnicoId);
      if (!map[key]) map[key] = [];
      map[key].push(a);
    }
    return map;
  }, [ausencias, allUsers]);

  const handleDownloadPDF = async () => {
    setPdfLoading(true);
    try {
      await generatePDF(selectedMonth, fichajesByTecnicoMonth, ausencias, tecnicos);
    } catch (e) { console.error('PDF error:', e); } finally { setPdfLoading(false); }
  };

  const reload = () => { loadFichajes(); loadAusencias(); };

  const [resolviendoId, setResolviendoId] = useState(null);
  const resolverAusencia = async (id, accion) => {
    setResolviendoId(id);
    try {
      const resp = await fetch('/.netlify/functions/manageAusencias', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify({ action: accion, id }),
      });
      const data = await resp.json();
      if (!data.success) alert('Error: ' + (data.error || 'desconocido'));
      await loadAusencias();
    } catch (e) {
      alert('Error: ' + e.message);
    } finally {
      setResolviendoId(null);
    }
  };

  // ── Sin fichar hoy (para badge en header) ────
  const sinFicharHoy = tab === 'diario' && selectedDate === today
    ? allUsers.filter(u => {
        const uid = getUserFichajeId(u);
        return !fichajeByTecnico[uid] && !getAusenciaForDate(uid, today);
      }).length
    : 0;

  return (
    <div className="space-y-6">

      {/* ── Header ── */}
      <div className="bg-white rounded-2xl shadow-lg border p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center flex-shrink-0">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              Control de Fichajes
              {sinFicharHoy > 0 && (
                <span className="text-xs bg-red-100 text-red-700 font-bold px-2.5 py-1 rounded-full">
                  {sinFicharHoy} sin fichar
                </span>
              )}
            </h2>
            <p className="text-gray-500 text-sm mt-1">Registro de jornada · RDL 8/2019 · Conservar 4 años</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={handleDownloadPDF}
              disabled={pdfLoading}
              className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-semibold rounded-xl transition-colors"
            >
              {pdfLoading
                ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                : <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
              }
              PDF — {formatMonthYear(selectedMonth)}
            </button>
            <button
              onClick={() => window.open(`/.netlify/functions/exportFichajesCSV?mes=${selectedMonth}&token=${encodeURIComponent(authService.getToken() || '')}`, '_blank')}
              className="flex items-center gap-2 px-4 py-2.5 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold rounded-xl transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
              CSV — {formatMonthYear(selectedMonth)}
            </button>
          </div>
        </div>
      </div>

      {/* ── Tabs ── */}
      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        <div className="flex border-b border-gray-100">
          {[
            { id: 'diario',   label: 'Vista diaria'    },
            { id: 'mensual',  label: 'Resumen mensual' },
            { id: 'ausencias',label: 'Ausencias'       },
          ].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`flex-1 py-3.5 text-sm font-semibold transition-colors ${
                tab === t.id ? 'text-red-600 border-b-2 border-red-600 bg-red-50/50' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="p-5">

          {/* ════ DIARIO ════ */}
          {tab === 'diario' && (
            <div className="space-y-4">
              {/* Navegador fecha */}
              <div className="flex items-center justify-between">
                <button onClick={() => setSelectedDate(d => adjustDate(d, -1))} className="p-2 hover:bg-gray-100 rounded-xl text-gray-500 transition-colors">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                </button>
                <div className="text-center">
                  <p className="font-bold text-gray-800 capitalize text-sm">{formatDateLong(selectedDate)}</p>
                  {selectedDate === today && <span className="text-[11px] bg-red-100 text-red-600 font-bold px-2 py-0.5 rounded-full">Hoy</span>}
                </div>
                <button onClick={() => setSelectedDate(d => adjustDate(d, 1))} className="p-2 hover:bg-gray-100 rounded-xl text-gray-500 transition-colors">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                </button>
              </div>

              {/* Badges resumen */}
              <div className="grid grid-cols-4 gap-2">
                {[
                  { label: 'Cerrados',  val: fichajes.filter(f => f.estado === 'cerrado' || f.estado === 'corregido').length, cls: 'bg-green-50 text-green-700' },
                  { label: 'En jornada',val: fichajes.filter(f => f.estado === 'activo').length, cls: 'bg-amber-50 text-amber-700' },
                  { label: 'Ausentes',  val: allUsers.filter(u => getAusenciaForDate(getUserFichajeId(u), selectedDate)).length, cls: 'bg-blue-50 text-blue-700' },
                  { label: 'Sin fichar',val: allUsers.filter(u => { const uid = getUserFichajeId(u); return !fichajeByTecnico[uid] && !getAusenciaForDate(uid, selectedDate); }).length, cls: 'bg-gray-50 text-gray-500' },
                ].map(b => (
                  <div key={b.label} className={`rounded-xl p-3 text-center ${b.cls}`}>
                    <p className="text-xl font-bold">{b.val}</p>
                    <p className="text-xs font-semibold mt-0.5">{b.label}</p>
                  </div>
                ))}
              </div>

              {loading ? (
                <div className="flex justify-center py-10"><div className="w-6 h-6 border-2 border-gray-200 border-t-red-500 rounded-full animate-spin" /></div>
              ) : allUsers.length === 0 ? (
                <div className="flex justify-center py-10"><div className="w-6 h-6 border-2 border-gray-200 border-t-red-500 rounded-full animate-spin" /></div>
              ) : (
                <div className="space-y-5">
                  {roleGroups.map(group => (
                    <div key={group.role}>
                      <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider px-1 mb-2">{group.label}</p>
                      <div className="space-y-2">
                        {group.users.map(u => {
                          const uid = getUserFichajeId(u);
                          const f   = fichajeByTecnico[uid];
                          const aus = getAusenciaForDate(uid, selectedDate);
                          const tipo = aus ? getTipo(aus.tipo) : null;
                          const rowBg = aus
                            ? `${tipo.bg} border-opacity-50`
                            : f?.estado === 'cerrado' || f?.estado === 'corregido' ? 'bg-green-50 border-green-100'
                            : f?.estado === 'activo' ? 'bg-amber-50 border-amber-100'
                            : 'bg-gray-50 border-gray-100';
                          const avatarBg = aus ? tipo.dot
                            : f?.estado === 'cerrado' || f?.estado === 'corregido' ? 'bg-green-500'
                            : f?.estado === 'activo' ? 'bg-amber-500'
                            : 'bg-gray-300';
                          const tecnicoModal = { id: uid, nombre: u.name };
                          return (
                            <div key={u.id} className={`flex items-center gap-3 p-4 rounded-xl border ${rowBg}`}>
                              <div className={`w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0 ${avatarBg}`}>
                                {u.name[0].toUpperCase()}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-semibold text-gray-900 text-sm">{u.name}</p>
                                {aus ? (
                                  <p className={`text-xs font-semibold mt-0.5 ${tipo.text}`}>
                                    {tipo.label} · {aus.fechaInicio}{aus.fechaFin !== aus.fechaInicio ? ` → ${aus.fechaFin}` : ''}
                                    {aus.notas ? ` · ${aus.notas}` : ''}
                                  </p>
                                ) : f ? (
                                  <div className="flex flex-wrap gap-x-3 mt-0.5">
                                    <span className="text-xs text-gray-600">Entrada: <strong>{f.entradaHora || '—'}</strong></span>
                                    {f.salidaHora && <span className="text-xs text-gray-600">Salida: <strong>{f.salidaHora}</strong></span>}
                                    {f.duracionMinutos != null && (
                                      <span className="text-xs text-gray-600">
                                        Efectivo: <strong>{minToDisplay(f.duracionMinutos)}</strong>
                                        {f.duracionDescansoMinutos > 0 && <span className="text-gray-400 ml-1">(- 1h descanso)</span>}
                                      </span>
                                    )}
                                  </div>
                                ) : (
                                  <p className="text-xs text-gray-400 mt-0.5">Sin registro</p>
                                )}
                              </div>
                              <div className="flex items-center gap-2 flex-shrink-0">
                                {aus ? (
                                  <>
                                    <span className={`inline-flex items-center gap-1 text-xs font-semibold ${tipo.text}`}>
                                      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${tipo.dot}`} />
                                      {tipo.label}
                                    </span>
                                    <button onClick={() => setAusenciaModal({ ausencia: aus, fechaDefecto: selectedDate })}
                                      className="p-1.5 hover:bg-white rounded-lg text-gray-400 hover:text-gray-700 transition-colors">
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                                    </button>
                                  </>
                                ) : (
                                  <>
                                    <EstadoBadge fichaje={f} />
                                    <button onClick={() => setFichajeModal({ tecnico: tecnicoModal, fichaje: f || null })}
                                      title={f ? 'Corregir fichaje' : 'Añadir fichaje'}
                                      className="p-1.5 hover:bg-white rounded-lg text-gray-400 hover:text-gray-700 transition-colors">
                                      {f
                                        ? <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                                        : <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                                      }
                                    </button>
                                    <button onClick={() => setAusenciaModal({ ausencia: null, fechaDefecto: selectedDate, tecnicoPreset: tecnicoModal })}
                                      title="Registrar ausencia"
                                      className="p-1.5 hover:bg-blue-50 rounded-lg text-gray-400 hover:text-blue-600 transition-colors">
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                                    </button>
                                  </>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ════ MENSUAL ════ */}
          {tab === 'mensual' && (
            <div className="space-y-5">
              <div className="flex items-center justify-between">
                <button onClick={() => setSelectedMonth(m => adjustMonth(m, -1))} className="p-2 hover:bg-gray-100 rounded-xl text-gray-500 transition-colors">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                </button>
                <p className="font-bold text-gray-800 capitalize">{formatMonthYear(selectedMonth)}</p>
                <button onClick={() => setSelectedMonth(m => adjustMonth(m, 1))} className="p-2 hover:bg-gray-100 rounded-xl text-gray-500 transition-colors">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                </button>
              </div>

              {loading ? (
                <div className="flex justify-center py-10"><div className="w-6 h-6 border-2 border-gray-200 border-t-red-500 rounded-full animate-spin" /></div>
              ) : (
                <>
                  <div className="overflow-x-auto rounded-xl border border-gray-100">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>{['Empleado','Rol','Días fichados','Ausencias','Total efectivo','Media/día'].map(h => <th key={h} className="py-3 px-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">{h}</th>)}</tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {ROLE_GROUPS.map(g => {
                          const rows = monthlyStats.filter(s => s.role === g.role);
                          if (!rows.length) return null;
                          return [
                            <tr key={`hdr-${g.role}`}><td colSpan={6} className="py-2 px-4 text-[11px] font-bold text-gray-400 uppercase tracking-wider bg-gray-50/80">{g.label}</td></tr>,
                            ...rows.map(s => (
                              <tr key={s.id} className="hover:bg-gray-50 transition-colors">
                                <td className="py-3 px-4 font-semibold text-gray-800">{s.nombre}</td>
                                <td className="py-3 px-4 text-xs text-gray-500">{s.grupo}</td>
                                <td className="py-3 px-4"><span className="inline-block bg-blue-100 text-blue-700 font-bold text-xs px-2.5 py-1 rounded-full">{s.dias} día{s.dias !== 1 ? 's' : ''}</span></td>
                                <td className="py-3 px-4"><span className="inline-block bg-purple-100 text-purple-700 font-bold text-xs px-2.5 py-1 rounded-full">{s.ausCount}</span></td>
                                <td className="py-3 px-4 font-semibold text-gray-800">{minToDisplay(s.totalMin)}</td>
                                <td className="py-3 px-4 text-gray-600">{s.dias > 0 ? minToDisplay(Math.round(s.totalMin / s.dias)) : '—'}</td>
                              </tr>
                            )),
                          ];
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Calendarios por usuario */}
                  <div className="space-y-3">
                    {allUsers.map(u => {
                      const uid = getUserFichajeId(u);
                      const tecMap = fichajesByTecnicoMonth[uid] || {};
                      return (
                        <div key={u.id} className="bg-gray-50 rounded-xl p-3">
                          <p className="text-xs font-bold text-gray-600 mb-2">{u.name}</p>
                          <div className="flex flex-wrap gap-1">
                            {calendarDays.map(dateStr => {
                              const day  = Number(dateStr.split('-')[2]);
                              const f    = tecMap[dateStr];
                              const aus  = getAusenciaForDate(String(uid), dateStr);
                              const tipo = aus ? getTipo(aus.tipo) : null;
                              const cellCls = aus        ? tipo.calBg
                                : f?.estado === 'cerrado'   ? 'bg-green-500 text-white'
                                : f?.estado === 'corregido' ? 'bg-blue-500 text-white'
                                : f?.estado === 'activo'    ? 'bg-amber-400 text-white'
                                :                             'bg-gray-200 text-gray-400';
                              const title = aus
                                ? `${day}: ${tipo.label}`
                                : f ? `${day}: ${f.entradaHora || '?'} → ${f.salidaHora || 'pendiente'}`
                                : `${day}: Sin fichar`;
                              return (
                                <div key={dateStr} title={title}
                                  className={`w-7 h-7 rounded-md flex items-center justify-center text-[10px] font-bold cursor-default ${cellCls}`}>
                                  {day}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                    <div className="flex flex-wrap gap-4 text-xs text-gray-400 pt-1">
                      <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-green-500 inline-block" />Cerrado</span>
                      <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-amber-400 inline-block" />En jornada</span>
                      <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-blue-500 inline-block" />Corregido</span>
                      {TIPOS_AUSENCIA.map(t => <span key={t.id} className="flex items-center gap-1"><span className={`w-3 h-3 rounded ${t.dot} inline-block`} />{t.label}</span>)}
                      <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-gray-200 inline-block" />Sin fichar</span>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* ════ AUSENCIAS ════ */}
          {tab === 'ausencias' && (
            <div className="space-y-5">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-2">
                  <button onClick={() => setSelectedMonth(m => adjustMonth(m, -1))} className="p-2 hover:bg-gray-100 rounded-xl text-gray-500 transition-colors">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                  </button>
                  <p className="font-bold text-gray-800 capitalize">{formatMonthYear(selectedMonth)}</p>
                  <button onClick={() => setSelectedMonth(m => adjustMonth(m, 1))} className="p-2 hover:bg-gray-100 rounded-xl text-gray-500 transition-colors">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                  </button>
                </div>
                <button
                  onClick={() => setAusenciaModal({ ausencia: null, fechaDefecto: selectedDate })}
                  className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold rounded-xl transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                  Registrar ausencia
                </button>
              </div>

              <div className="space-y-4">
                {allUsers.map(u => {
                  const uid = getUserFichajeId(u);
                  const aus = ausenciasByTecnico[uid] || [];
                  const tecnicoModal = { id: uid, nombre: u.name };
                  return (
                    <div key={u.id} className="border border-gray-100 rounded-xl overflow-hidden">
                      <div className="bg-gray-50 px-4 py-2.5 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-full bg-red-600 text-white flex items-center justify-center font-bold text-xs flex-shrink-0">{u.name[0].toUpperCase()}</div>
                          <div>
                            <p className="font-semibold text-gray-800 text-sm">{u.name}</p>
                            <p className="text-[10px] text-gray-400">{ROLE_GROUPS.find(g => g.role === u.role)?.label || u.role}</p>
                          </div>
                        </div>
                        <button
                          onClick={() => setAusenciaModal({ ausencia: null, fechaDefecto: selectedDate, tecnicoPreset: tecnicoModal })}
                          className="flex items-center gap-1 text-xs text-red-600 hover:text-red-700 font-semibold"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                          Añadir
                        </button>
                      </div>
                      {aus.length === 0 ? (
                        <p className="text-xs text-gray-400 text-center py-4">Sin ausencias registradas</p>
                      ) : (
                        <div className="divide-y divide-gray-50">
                          {aus.map(a => {
                            const tipo = getTipo(a.tipo);
                            const dias = Math.round((new Date(a.fechaFin) - new Date(a.fechaInicio)) / 86400000) + 1;
                            const pendiente = a.estado === 'pendiente';
                            const resolviendo = resolviendoId === a.id;
                            return (
                              <div key={a.id} className={`px-4 py-3 flex items-center gap-3 ${pendiente ? 'bg-amber-50/60' : ''}`}>
                                <span className={`inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full border ${tipo.bg} ${tipo.text}`}>
                                  {tipo.label}
                                </span>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm text-gray-800">
                                    {a.fechaInicio === a.fechaFin ? a.fechaInicio : `${a.fechaInicio} → ${a.fechaFin}`}
                                    <span className="text-gray-400 text-xs ml-2">({dias} día{dias !== 1 ? 's' : ''})</span>
                                    {pendiente && <span className="ml-2 text-[10px] font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-full">Pendiente de aprobar</span>}
                                    {a.estado === 'rechazada' && <span className="ml-2 text-[10px] font-bold text-red-700 bg-red-100 px-1.5 py-0.5 rounded-full">Rechazada</span>}
                                  </p>
                                  {a.notas && <p className="text-xs text-gray-400 truncate mt-0.5">{a.notas}</p>}
                                  {a.creadoPor === 'tecnico' && <p className="text-[10px] text-gray-400 mt-0.5">Solicitada por el propio técnico</p>}
                                </div>
                                {pendiente ? (
                                  <div className="flex items-center gap-1.5 flex-shrink-0">
                                    <button
                                      onClick={() => resolverAusencia(a.id, 'aprobar')}
                                      disabled={resolviendo}
                                      className="px-2.5 py-1.5 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white text-xs font-bold rounded-lg transition-colors"
                                    >
                                      Aprobar
                                    </button>
                                    <button
                                      onClick={() => resolverAusencia(a.id, 'rechazar')}
                                      disabled={resolviendo}
                                      className="px-2.5 py-1.5 bg-white border border-gray-200 hover:bg-gray-50 disabled:opacity-50 text-gray-600 text-xs font-bold rounded-lg transition-colors"
                                    >
                                      Rechazar
                                    </button>
                                  </div>
                                ) : (
                                  <button
                                    onClick={() => setAusenciaModal({ ausencia: a, fechaDefecto: a.fechaInicio })}
                                    className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-gray-700 transition-colors flex-shrink-0"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                                  </button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Modales ── */}
      {fichajeModal && (
        <FichajeModal
          fichaje={fichajeModal.fichaje}
          tecnico={fichajeModal.tecnico}
          fecha={selectedDate}
          onClose={() => setFichajeModal(null)}
          onSaved={() => { setFichajeModal(null); reload(); }}
          userName={user?.name}
        />
      )}
      {ausenciaModal && (
        <AusenciaModal
          ausencia={ausenciaModal.ausencia}
          fechaDefecto={ausenciaModal.fechaDefecto}
          tecnicoPreset={ausenciaModal.tecnicoPreset}
          allUsers={allUsers}
          onClose={() => setAusenciaModal(null)}
          onSaved={() => { setAusenciaModal(null); reload(); }}
          userName={user?.name}
        />
      )}
    </div>
  );
}
